import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { setAccessAllowed } from '../services/subscriptionService';
import './SubscriptionPage.css';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isManual = new URLSearchParams(location.search).get('manual') === '1';
  const hasAutoRedirected = useRef(false);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [showOperatorSelection, setShowOperatorSelection] = useState(isManual);
  const [operatorMessage, setOperatorMessage] = useState('');

  useEffect(() => {
    // Auto-redirect to AIS MSISDN forwarding on load (unless ?manual=1 fallback),
    // matching the gamerclub.mobi/subscription behavior.
    if (!isManual && !hasAutoRedirected.current) {
      hasAutoRedirected.current = true;
      const keyword = process.env.REACT_APP_AIS_KEYWORD || 'YG2';
      const shortcode = process.env.REACT_APP_AIS_SHORTCODE || '4541392';
      // Preserve tracking params (pid, clickid, ad_id, etc.) through the forwarding flow.
      const params = new URLSearchParams(location.search);
      params.delete('manual');
      const extra = params.toString();
      // Force http:// for the callback (eTracker requires the AIS callback on plain HTTP).
      const callbackBase = `http://${window.location.hostname}/subscription/msisdn-callback${extra ? '?' + extra : ''}`;
      const callbackUrl = encodeURIComponent(callbackBase);
      window.location.href = `http://mis.etracker.cc/THWAP/MsisdnForwarding.aspx?Keyword=${keyword}&Shortcode=${shortcode}&CallBackURL=${callbackUrl}`;
    }
  }, [isManual, location.search]);

  const handleGoBack = () => {
    navigate(-1);
  };
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = () => {
    // Show operator selection instead of direct subscription
    setShowOperatorSelection(true);
    setOperatorMessage('Please select your mobile operator to continue with subscription');
  };
  
  const handleOperatorSelect = (operator) => {
    setSelectedOperator(operator);
    setIsLoading(true);

    if (operator.toLowerCase() === 'ais') {
      // AIS uses eTracker MSISDN Forwarding so the subscriber's MSISDN is identified
      // before the WAP MO. Preserve tracking params (pid, clickid, ad_id, etc.) and
      // force http:// for the callback (eTracker requires the AIS callback on plain HTTP).
      const keyword = process.env.REACT_APP_AIS_KEYWORD || 'YG2';
      const shortcode = process.env.REACT_APP_AIS_SHORTCODE || '4541392';
      const params = new URLSearchParams(window.location.search);
      params.delete('manual');
      const extra = params.toString();
      const callbackBase = `http://${window.location.hostname}/subscription/msisdn-callback${extra ? '?' + extra : ''}`;
      const callbackUrl = encodeURIComponent(callbackBase);
      window.location.href = `http://mis.etracker.cc/THWAP/MsisdnForwarding.aspx?Keyword=${keyword}&Shortcode=${shortcode}&CallBackURL=${callbackUrl}`;
      return;
    }

    // Redirect to the selected operator's subscription page (DTAC flow unchanged)
    setTimeout(() => {
      navigate(`/subscription/${operator.toLowerCase()}`);
    }, 500);
  };
  
  const handleDirectSubscribe = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Call the subscription API (GET request)
      const apiUrl = process.env.REACT_APP_AUTH_TOKEN_API || 'https://api.gamerclub.mobi/api/dn/th/auth/token';
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error('Subscription failed. Please try again.');
      }
      
      const data = await response.json();
      
      // Get the token from the response
      const token = data.token || '';
      
      // Set access allowed to true after successful subscription
      setAccessAllowed(true);
      
      // Redirect to the home page with success message
      alert('Subscription successful! You now have access to all premium games.');
      navigate('/');
    } catch (err) {
      setError(err.message || 'An error occurred during subscription');
      console.error('Subscription error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <button onClick={handleGoBack} className="back-button">
          ← กลับไปที่เกม
        </button>
        <h1>🎮 สมัครสมาชิก</h1>
      </div>
      
      <div className="subscription-content">
        {/* Subscription info box removed */}
        
        <div className="subscription-plans">
          {/* Plan toggle buttons removed */}
          
          <div className="plan-details">
            <h3>Premium Subscription</h3>
            
            <div className="game-image-container">
              <img src="/image.png" alt="Subscription Image" className="subscription-image" />
            </div>
            
            <div className="subscription-details">
              <p>
                บริการ HTML5 Game อัตราค่าบริการ 10 บาทต่อ SMS คุณจะได้รับ 1 SMS ต่อ วัน
              </p>
              <p>
                หรือ สมัครโดยพิมพ์ GBR1 ส่งมาที่ 4541396<br/>
                สอบถามโทร : 02-1158814, 9.00 - 18.00 (จันทร์ - ศุกร์)<br/>
                ยกเลิกพิมพ์ STOP GBR1 ส่งมาที่ 4541396
              </p>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            {!showOperatorSelection ? (
              <button 
                className={`subscribe-button ${isLoading ? 'loading' : ''}`} 
                onClick={handleSubscribe}
                disabled={isLoading}
              >
                {isLoading ? 'กำลังดำเนินการ...' : 'สมัครสมาชิก'}
              </button>
            ) : (
              <div className="operator-selection">
                <p className="operator-message">กรุณาเลือกผู้ให้บริการโทรศัพท์มือถือของคุณ</p>
                <div className="carrier-buttons">
                  <button 
                    className={`carrier-button ais ${selectedOperator === 'ais' ? 'selected' : ''}`}
                    onClick={() => handleOperatorSelect('ais')}
                    disabled={isLoading}
                  >
                    AIS
                  </button>
                  <button 
                    className={`carrier-button dtac ${selectedOperator === 'dtac' ? 'selected' : ''}`}
                    onClick={() => handleOperatorSelect('dtac')}
                    disabled={isLoading}
                  >
                    DTAC
                  </button>
                </div>
                <button 
                  className="back-button small" 
                  onClick={() => setShowOperatorSelection(false)}
                  disabled={isLoading}
                >
                  ยกเลิก
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;